import { useRef, useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const CASES = [
  {
    company: "Fortune 500 Data Infrastructure Company",
    sub: "Thousands of Products. Millions of Combinations. Powered by Agentic Intelligence.",
    desc: "An AI-powered compatibility agent that helps 15,000+ partners instantly identify compatible products and build complex HW/SW configurations.",
    impact: [
      { n: "95%+", l: "Faster Configuration", d: "Turnaround cut from 3\u20134 days to 1\u20132 hours" },
      { n: "15K+", l: "Partners Enabled", d: "With real-time product compatibility and configuration guidance" },
    ],
    tags: ["On-Premise", "Product \u00B7 Configuration & Compatibility"],
    feat: true,
  },
  {
    company: "Global Healthcare Workforce Solutions Company",
    sub: "Find the Right Healthcare Professionals in Seconds \u2014 Across Millions of Profiles",
    desc: "An agentic sales intelligence solution that discovers highly specific healthcare professionals across millions of records via conversational, multi-attribute search.",
    impact: [
      { n: "3\u00D7", l: "More Qualified Prospects", d: "Teams engage far more relevant professionals within the same prospecting capacity" },
      { n: "25%+", l: "Higher Campaign Conversion", d: "Precise targeting across speciality, experience and geography lifts outreach effectiveness" },
    ],
    tags: ["Customer Cloud", "Sales \u00B7 Prospect Discovery"],
  },
  {
    company: "Fortune 500 Enterprise Technology Company",
    sub: "Turn Product Telemetry into Adoption and Growth Intelligence",
    desc: "An agentic product intelligence solution helping leaders understand utilization, adoption and expansion across telemetry, sales and install-base data.",
    impact: [
      { n: "20%+", l: "Higher Product Adoption", d: "Accounts activated on underutilized products using usage and adoption intelligence" },
      { n: "30%+", l: "More Expansion Opportunities", d: "Whitespace, cross-sell and upsell surfaced from telemetry and install-base signals" },
    ],
    tags: ["On-Premise", "AI over BI"],
  },
  {
    company: "Global Health Technology Company",
    sub: "Proactively Improving Digital Experience for a 70,000-Strong Workforce",
    desc: "An agentic employee experience solution that spots workforce technology issues and proactively guides support, updates and resolution.",
    impact: [
      { n: "20%+", l: "Fewer IT Support Tickets", d: "Recurring issues resolved before they escalate into support requests" },
      { n: "15%+", l: "Lower Technology Downtime", d: "Less productivity loss from device, app and software-update issues" },
    ],
    tags: ["Customer Cloud", "HR \u00B7 Employee Experience"],
  },
  {
    company: "Leading Automotive Manufacturer",
    sub: "A Shared Enterprise Brain for Every AI Agent and Application",
    desc: "A living enterprise context layer that generates, validates and retrieves business knowledge, giving every AI agent trusted context to reason and act.",
    impact: [
      { n: "60%+", l: "Less Duplicate Context Engineering", d: "Shared semantics and knowledge reused instead of rebuilt per initiative" },
      { n: "3\u00D7", l: "More AI Use Cases Supported", d: "One context layer runs far more agents and apps on a single foundation" },
    ],
    tags: ["On-Premise", "Enterprise Brain"],
    feat: true,
  },
  {
    company: "Leading Consumer Foods Company",
    sub: "Markets. Competitors. Field Performance. Decisions at the Frontline.",
    desc: "An agentic sales intelligence solution helping field teams read competitive performance and take context-aware actions across territories and outlets.",
    impact: [
      { n: "15\u201320%", l: "Sales Uplift", d: "Better frontline actions from competitor gaps and product opportunities" },
      { n: "10%+", l: "Higher Competitive Win Rate", d: "Targeted interventions where competitor signals show opportunity" },
    ],
    tags: ["Customer Cloud", "Sales \u00B7 Competitive Intelligence"],
  },
  {
    company: "Global HVAC Company",
    sub: "From Complex Data Pipelines to Reusable, Trusted Data Products",
    desc: "An agentic data engineering solution that builds reusable, trusted data products while cutting pipeline complexity and analytics infrastructure cost.",
    impact: [
      { n: "40%+", l: "Lower Data Platform TCO", d: "Less pipeline duplication, compute and maintenance across the data ecosystem" },
      { n: "2\u00D7", l: "Data Asset Reuse", d: "Trusted data products reused across analytics, agents and apps instead of rebuilt" },
    ],
    tags: ["Customer Cloud", "Data Products"],
  },
];

const Card = ({ c }) => {
  const dark = c.feat;
  return (
    <div
      className={`shrink-0 w-[320px] sm:w-[400px] snap-start rounded-2xl p-7 flex flex-col ${dark ? "bg-gradient-to-b from-[#2E1B0C] to-[#160D05] text-[#F7F5F0] ring-1 ring-[#FFB84D]/40" : "bg-white border border-[#3E2818]/10 text-[#3E2818]"}`}
      data-testid={`case-card-${c.feat ? "flagship" : "std"}`}
    >
      <h4 className={`font-display text-xl font-medium leading-tight ${dark ? "text-white" : "text-[#3E2818]"}`}>{c.company}</h4>
      <p className={`text-sm font-medium mt-2.5 leading-snug ${dark ? "text-[#FFB84D]" : "text-[#8A5730]"}`}>{c.sub}</p>
      <p className={`text-[13px] mt-3 leading-relaxed ${dark ? "text-white/70" : "text-[#3E2818]/65"}`}>{c.desc}</p>

      <div className={`h-px my-6 ${dark ? "bg-white/15" : "bg-[#3E2818]/10"}`} />

      <p className={`text-[10px] font-mono2 uppercase tracking-[0.24em] mb-4 ${dark ? "text-white/40" : "text-[#3E2818]/40"}`}>Impact</p>
      <div className="space-y-5">
        {c.impact.map((m) => (
          <div key={m.l}>
            <div className="flex items-baseline gap-2.5">
              <span className={`font-display text-[26px] font-medium leading-none ${dark ? "text-[#FFB84D]" : "text-[#E0902B]"}`}>{m.n}</span>
              <span className={`text-[13px] font-semibold leading-tight ${dark ? "text-white" : "text-[#3E2818]"}`}>{m.l}</span>
            </div>
            <p className={`text-[12px] mt-1.5 leading-relaxed ${dark ? "text-white/55" : "text-[#3E2818]/55"}`}>{m.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-7 flex flex-wrap gap-2">
        {c.tags.map((t) => (
          <span key={t} className={`text-[11px] font-semibold rounded-full px-3 py-1 ${dark ? "bg-[#8A5730]/25 text-[#EDC79A] ring-1 ring-[#FFB84D]/25" : "bg-[#EFE7D9] text-[#8A5730] ring-1 ring-[#8A5730]/15"}`}>{t}</span>
        ))}
      </div>
    </div>
  );
};

export const CaseStudies = () => {
  const ref = useRef(null);
  const pausedRef = useRef(false);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 420, behavior: "smooth" });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!pausedRef.current) {
        el.scrollLeft += 0.6;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section id="success-stories" className="bg-[#F7F5F0] py-20 lg:py-28" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-6xl mb-8">
          <Reveal delay={0.02}><p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Proven in Production</p></Reveal>
          <Reveal delay={0.04}><h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl">Where Agentic AI Delivers Real Business Impact</h2></Reveal>
          <Reveal delay={0.12}><p className="mt-4 text-base text-[#3E2818]/65 leading-relaxed max-w-2xl">See how leading enterprises are putting agentic applications into production at scale — powered by Intugle across critical business functions, data ecosystems and decision workflows.</p></Reveal>
        </div>
        <div className="hidden sm:flex justify-end gap-2 mb-6">
          <button onClick={() => scroll(-1)} data-testid="case-prev" aria-label="Previous" className="w-12 h-12 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors duration-300"><ArrowLeft size={18} /></button>
          <button onClick={() => scroll(1)} data-testid="case-next" aria-label="Next" className="w-12 h-12 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors duration-300"><ArrowRight size={18} /></button>
        </div>
      </div>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 sm:w-10 bg-gradient-to-r from-[#F7F5F0]/35 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 sm:w-10 bg-gradient-to-l from-[#F7F5F0]/35 to-transparent" />
        <div
          ref={ref}
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          className="flex gap-5 overflow-x-auto pb-4 px-5 sm:px-8 lg:px-12 items-stretch [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="case-carousel"
        >
          {[...CASES, ...CASES].map((c, i) => <Card key={i} c={c} />)}
        </div>
      </div>
    </section>
  );
};
